import { useCallback } from "react"

import type { DurableChatTurnIdentity } from "@/api/models"

import { sendDurableChatTurnControlApi } from "@/api/api"
import { decideDeepToolApproval } from "@/features/ai-explorer/utils/deep-tool-approvals"
import { buildDurableChatTurnClientControlId } from "@/features/chat/hooks/durable-chat-turn-state"
import {
  dumpWebSocketRegistry,
  getLastRegisteredWebSocket,
  getSessionWebSocket,
} from "@/features/chat/utils/websocket-session-registry"
import { logger } from "@/lib/logger"

type DecisionPayload = {
  request_id: string
  call_id: string
  approved: boolean
  reason?: string
}

export type DurableToolApprovalBinding = {
  durableTurn: DurableChatTurnIdentity
  getToken: () => string | undefined
}

/**
 * `durable`, when provided, binds every decision to exactly that turn
 * (captured by the caller, not re-resolved here) via a deterministic
 * `clientControlId` — a retried or duplicate click can never mint a second
 * control, and a late response can never be mistaken for a decision on a
 * newer turn. Omitting it (the default for every existing call site)
 * preserves the legacy WebSocket-registry send byte-for-byte — this is
 * additive, not a behavior change.
 */
export function useToolApprovalActions(
  sessionId: string | null,
  fallbackSessionId?: string | null,
  durable?: DurableToolApprovalBinding | null
) {
  const sendDecision = useCallback(
    async (payload: DecisionPayload) => {
      // Deep Explorer approvals answer over the Deep socket, not the chat
      // socket or a durable control (#2962). The registry knows the sender.
      if (decideDeepToolApproval(payload.request_id, payload.approved)) {
        return
      }
      if (durable) {
        const { durableTurn, getToken } = durable
        const clientControlId = buildDurableChatTurnClientControlId({
          turnId: durableTurn.turnId,
          requestId: payload.request_id,
          callId: payload.call_id,
          approved: payload.approved,
        })
        await sendDurableChatTurnControlApi(
          durableTurn.sessionId,
          {
            turnId: durableTurn.turnId,
            clientControlId,
            action: "tool_decision",
            requestId: payload.request_id,
            callId: payload.call_id,
            approved: payload.approved,
            reason: payload.reason,
          },
          getToken()
        )
        return
      }

      const candidateIds = [sessionId, fallbackSessionId].filter((value): value is string =>
        Boolean(value)
      )
      if (candidateIds.length === 0) {
        logger.error("Tool approval decision attempted without session context", {
          requestId: payload.request_id,
          callId: payload.call_id,
        })
        throw new Error("No active session for tool approval decision")
      }

      let client: ReturnType<typeof getSessionWebSocket> | undefined
      let resolvedSessionId: string | null = null
      let fallbackSnapshot: ReturnType<typeof getLastRegisteredWebSocket> | undefined
      let usedFallback = false
      for (const candidate of candidateIds) {
        client = getSessionWebSocket(candidate)
        if (client) {
          resolvedSessionId = candidate
          break
        }
      }

      if (!client || !resolvedSessionId) {
        fallbackSnapshot = getLastRegisteredWebSocket()
        if (fallbackSnapshot?.client) {
          client = fallbackSnapshot.client
          resolvedSessionId = fallbackSnapshot.sessionId ?? null
          usedFallback = true
        }
      }

      if (!client || !resolvedSessionId) {
        logger.error("Tool approval decision WebSocket lookup failed", {
          requestId: payload.request_id,
          callId: payload.call_id,
          candidateIds,
          resolvedSessionId,
          usedFallback,
          lastRegisteredSessionId: fallbackSnapshot?.sessionId ?? null,
        })
        dumpWebSocketRegistry("tool-approval-decision-failure")
        throw new Error("WebSocket connection not found for session")
      }

      // Send tool_decision message via WebSocket
      const message = {
        type: "tool_decision",
        request_id: payload.request_id,
        call_id: payload.call_id,
        approved: payload.approved,
        reason: payload.reason,
      }

      client.send(message)
    },
    [fallbackSessionId, sessionId, durable]
  )

  const approve = useCallback(
    async (requestId: string, callId: string) => {
      await sendDecision({
        request_id: requestId,
        call_id: callId,
        approved: true,
      })
    },
    [sendDecision]
  )

  const deny = useCallback(
    async (requestId: string, callId: string, reason?: string) => {
      await sendDecision({
        request_id: requestId,
        call_id: callId,
        approved: false,
        reason,
      })
    },
    [sendDecision]
  )

  const approveAll = useCallback(
    async (approvals: Array<{ request_id: string; call_id: string }>) => {
      for (const approval of approvals) {
        await sendDecision({
          request_id: approval.request_id,
          call_id: approval.call_id,
          approved: true,
        })
      }
    },
    [sendDecision]
  )

  const denyAll = useCallback(
    async (approvals: Array<{ request_id: string; call_id: string }>, reason?: string) => {
      for (const approval of approvals) {
        await sendDecision({
          request_id: approval.request_id,
          call_id: approval.call_id,
          approved: false,
          reason,
        })
      }
    },
    [sendDecision]
  )

  return { approve, deny, approveAll, denyAll }
}
